"use client";

import React from "react";
import { Phone, MessageCircle, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { cn } from "@/lib/utils";
const ConversationForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="21584" className="h-full flex flex-col items-center justify-center text-center p-8 border border-primary/20 bg-primary/5 rounded-sm">
        <h3 className="text-2xl font-serif font-bold text-primary mb-2">Inquiry Received</h3>
        <p className="text-secondary/80 font-sans">Thank you for reaching out. A BVP Group consultant will contact you shortly to discuss your renovation.</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-0">
      <div className="space-y-6">
        {}
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Name" className="bg-transparent border-secondary/20 border-x-0 border-t-0 border-b text-secondary placeholder:text-secondary/50 rounded-none h-14 px-0 focus-visible:ring-0 focus-visible:border-primary text-lg font-sans" {...field} />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="Email Address" className="bg-transparent border-secondary/20 border-x-0 border-t-0 border-b text-secondary placeholder:text-secondary/50 rounded-none h-14 px-0 focus-visible:ring-0 focus-visible:border-primary text-lg font-sans" {...field} />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="Phone Number" className="bg-transparent border-secondary/20 border-x-0 border-t-0 border-b text-secondary placeholder:text-secondary/50 rounded-none h-14 px-0 focus-visible:ring-0 focus-visible:border-primary text-lg font-sans" {...field} />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        {}
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Budget Range (e.g. £20k - £100k+)</FormLabel>
              <FormControl>
                <Input placeholder="Budget Range (e.g. £20k - £100k+)" className="bg-transparent border-secondary/20 border-x-0 border-t-0 border-b text-secondary placeholder:text-secondary/50 rounded-none h-14 px-0 focus-visible:ring-0 focus-visible:border-primary text-lg font-sans" {...field} />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        {}
        <FormField control={control} name="message" rules={{
        required: "Please tell us how we can help"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Project Description</FormLabel>
              <FormControl>
                <textarea placeholder="Tell us about your project or inquiry..." className="flex min-h-[120px] w-full bg-transparent border-secondary/20 border-x-0 border-t-0 border-b text-secondary placeholder:text-secondary/50 rounded-none px-0 py-4 text-lg font-sans focus-visible:outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 resize-none" {...field} />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 mt-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm font-sans">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full mt-12 h-16 text-lg font-bold uppercase tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 rounded-none border-none shadow-lg transition-all">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Sending...
          </> : "Send Inquiry"}
      </Button>
    </Form>;
};
export default function StartAConversation() {
  return <section id="book-your-consultation" className="bg-secondary text-secondary-foreground py-20 lg:py-32 overflow-hidden relative">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {}
          <div className="flex flex-col justify-between h-full space-y-12">
            <div>
              <h2 className="text-7xl md:text-8xl lg:text-9xl font-serif font-semibold tracking-tighter leading-[0.85] mb-8 text-white uppercase">
                Start Your
                <br />
                <span className="text-primary">Project</span>
              </h2>
              <p className="text-xl md:text-2xl text-white/70 max-w-md font-sans font-light leading-relaxed">Transform your London home with BVP Group. Reach out for a free bespoke consultation.</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mt-auto">
              <Button asChild size="lg" className="h-14 px-8 text-base font-bold uppercase tracking-wide rounded-none border-2 border-white bg-transparent text-white hover:bg-white hover:text-secondary transition-colors">
                <Link to="tel:07894 282 884"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-phone mr-2 h-5 w-5" aria-hidden="true" data-ast-id="f:/src/pages/Contact/sections/StartAConversation.tsx|sig:f2jbf8" data-ast-start="7692" data-ast-end="7726"><path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"></path></svg>07894 282 884</Link>
              </Button>
              
              <Button asChild size="lg" className="h-14 px-8 text-base font-bold uppercase tracking-wide rounded-none bg-primary text-primary-foreground hover:bg-primary/90 border-none shadow-md">
                <Link to="https://wa.me/447894282884" newTab>
                  <MessageCircle className="mr-2 h-5 w-5" />
                  WhatsApp Us
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="relative">
            <div className="absolute -inset-4 bg-primary/10 blur-3xl -z-10 rounded-full opacity-30" />
            <div className="bg-white backdrop-blur-sm border border-white/20 p-8 md:p-12 shadow-2xl rounded-sm">
              <FormProvider formKey="consultation_form_contact" formId="consultation_form_contact" sectionName="book-your-consultation" defaultValues={{
              name: "",
              email: "",
              phone: "",
              company: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              message: {
                required: true
              }
            }} submitText="Send Inquiry" successMessage="Thank you! We've received your inquiry and will be in touch shortly to schedule your consultation.">
                <ConversationForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}